import { ReactNode, useCallback, useContext, useEffect } from 'react';

import { ProductNote, ProductQty, SelectedItemMap } from '@common/models';
import { getProductDescription } from '@common/utils';
import { productQtyFromSelectedItems } from '@common/utils/productQtyFromSelectedItems';
import useResetableState from '@core/hooks/useResetableState';
import createTypeSafeContext from '@core/utils/createTypeSafeContext';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useOptionalItems } from '@ee-api/optional-items-api/useOptionalItems';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { useAppSelector } from '@redux/hooks';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import { DrawingShapeContext } from './drawingShapeContext';

export interface OptionalProductContextValue {
    selectedOptionalProducts: SelectedItemMap;
    resetSelectedOptionalProducts: () => void;
    assignSelectedOptionalProducts: (partialState: SelectedItemMap) => void;
    getOptionalProductsProductQty: () => ProductQty[];
    restorableOptionalProducts: SelectedItemMap;
    setRestorableOptionalProducts: (value: SelectedItemMap) => void;
    makeRestorableOptionalProducts: () => SelectedItemMap;
    resetRestorableOptionalProducts: () => void;
    areOptionalProductsAvailable: () => boolean;
    makeOptionalProductNotes: () => ProductNote[];
    setSelectedOptionalProducts: (products: SelectedItemMap) => void;
}

export const [useOptionalProductContext, OptionalProductContext] = createTypeSafeContext<OptionalProductContextValue>();

export const OptionalProductContextProvider = ({ children }: { children?: ReactNode }): JSX.Element => {
    const zipcode = useAppSelector(selectZipcode);
    const { selectedValue: selectedStore } = useSelectedStore();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);

    const { optionalProducts } = useOptionalItems();

    const [selectedOptionalProducts, setSelectedOptionalProducts, resetSelectedOptionalProducts] =
        useResetableState<SelectedItemMap>(new Map());
    const [restorableOptionalProducts, setRestorableOptionalProducts, resetRestorableOptionalProducts] =
        useResetableState<SelectedItemMap>(new Map());

    const areOptionalProductsAvailable = () => {
        return Boolean(zipcode && selectedStore && selectedMaterial && areThereShapesInTheDrawingArea);
    };

    const getOptionalProductsProductQty = (): ProductQty[] => {
        return productQtyFromSelectedItems(selectedOptionalProducts);
    };

    const makeRestorableOptionalProducts = (): SelectedItemMap => {
        const newRestorableOptionalProducts: SelectedItemMap = new Map();
        for (const [optionalproductProductId, optionalproductSelection] of selectedOptionalProducts) {
            if (optionalproductSelection.isSelected && optionalproductSelection.quantity > 0) {
                newRestorableOptionalProducts.set(optionalproductProductId, optionalproductSelection);
            }
        }
        return newRestorableOptionalProducts;
    };

    const makeOptionalProductNotes = (): ProductNote[] => {
        return optionalProducts.reduce((productNotes, optionalProduct) => {
            const productSelection = selectedOptionalProducts.get(optionalProduct.productId);
            if (productSelection && productSelection.isSelected && productSelection.note) {
                productNotes.push({
                    productDescription: getProductDescription(optionalProduct),
                    note: productSelection.note,
                });
            }
            return productNotes;
        }, [] as ProductNote[]);
    };

    // Merge the current map of selected products with the incoming map.
    // Overwrite the value of conflicting keys.
    const assignSelectedOptionalProducts = (partialState: SelectedItemMap) => {
        setSelectedOptionalProducts((currState) => {
            return new Map([...currState, ...partialState]);
        });
    };

    const restoreSelectedOptionalProducts = useCallback(() => {
        const restoredSelection = optionalProducts.reduce<SelectedItemMap>((acc, optionalProduct) => {
            const restoredOptionalProduct = restorableOptionalProducts.get(optionalProduct.productId);
            if (restoredOptionalProduct) {
                acc.set(optionalProduct.productId, restoredOptionalProduct);
            }
            return acc;
        }, new Map());

        if (restoredSelection.size) {
            setSelectedOptionalProducts(restoredSelection);
        }

        resetRestorableOptionalProducts();
    }, [optionalProducts, restorableOptionalProducts, resetRestorableOptionalProducts, setSelectedOptionalProducts]);

    useEffect(() => {
        // Checking restorable map size prevents loop
        if (optionalProducts.length && restorableOptionalProducts.size) {
            restoreSelectedOptionalProducts();
        }
    }, [optionalProducts, restorableOptionalProducts, restoreSelectedOptionalProducts]);

    return (
        <OptionalProductContext.Provider
            value={{
                selectedOptionalProducts,
                resetSelectedOptionalProducts,
                assignSelectedOptionalProducts,
                getOptionalProductsProductQty,
                restorableOptionalProducts,
                setRestorableOptionalProducts,
                makeRestorableOptionalProducts,
                resetRestorableOptionalProducts,
                areOptionalProductsAvailable,
                makeOptionalProductNotes,
                setSelectedOptionalProducts,
            }}
        >
            {children}
        </OptionalProductContext.Provider>
    );
};
